import { BreakPointCondition, useBreakpointIf } from '@genial/components';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import { FilterModal, FilterModalTypes } from '../../../../components/FilterModal';
import { getEmptySelections } from '../../../../components/FilterModal/constants';
import {
  ReportGeneratorCategory,
  getDocumentTypesByCategoryId,
} from '../../../../store/reportGenerator/categories';
import { reportGeneratorFiltersAtom } from '../../../../store/reportGenerator/filters';
import { reportGeneratorCategoriesSelector } from '../../../../store/reportGenerator/selector';

const getCategoryId = (selectedFilters: FilterModalTypes.SelectedFiltersProps[]) => {
  const selection = selectedFilters.find((filter) => filter.fieldName === 'categoryId')?.filter;
  if (!Array.isArray(selection) || typeof selection[0] !== 'string') {
    return undefined;
  }
  return selection[0];
};

const buildAvailableFilters = (
  categories: ReportGeneratorCategory[],
  selectedCategoryId?: string
): FilterModalTypes.FilterType[] => [
  {
    type: 'tags',
    title: 'Categorias',
    fieldName: 'categoryId',
    options: categories.map((category) => ({ label: category.label, value: category.id })),
    onlyOneSelection: true,
  },
  {
    type: 'select',
    title: 'Nome do relatório',
    fieldName: 'documentTypeId',
    options: selectedCategoryId
      ? getDocumentTypesByCategoryId(categories, selectedCategoryId).map((documentType) => ({
          value: documentType.id,
          label: documentType.label,
        }))
      : [{ value: '', label: 'Selecione uma categoria', disabled: true }],
    customSelectProps: {
      placeholder: selectedCategoryId ? 'Nome do relatório' : 'Selecione uma categoria',
      disabled: !selectedCategoryId,
    },
  },
  {
    type: 'dateRange',
    title: 'Solicitação',
    fieldName: 'commonDateRange',
    placeholder: 'DD/MM/AAAA - DD/MM/AAAA',
    maxDaysRange: 365,
    calendarAvailableRange: { future: new Date() },
  },
  {
    type: 'section',
    title: 'Status',
    fieldName: 'statusId',
    options: [
      { label: 'Em processamento', value: '100' },
      { label: 'Concluído', value: '200' },
      { label: 'Erro no processo', value: '500' },
    ],
  },
];

export const ReportGeneratorFilterByModal = () => {
  const navigation = useNavigation();
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const [{ selectedFilters }, setFiltersState] = useRecoilState(reportGeneratorFiltersAtom);
  const categoriesLoadable = useRecoilValueLoadable(reportGeneratorCategoriesSelector);
  const previousCategoryIdRef = useRef<string | undefined>();

  const categories = useMemo(
    () => (categoriesLoadable.state === 'hasValue' ? categoriesLoadable.contents : []),
    [categoriesLoadable]
  );

  const selectedCategoryId = getCategoryId(selectedFilters);

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  useEffect(() => {
    const availableFilters = buildAvailableFilters(categories, selectedCategoryId);
    const categoryChanged = previousCategoryIdRef.current !== selectedCategoryId;
    previousCategoryIdRef.current = selectedCategoryId;

    setFiltersState((prev) => ({
      ...prev,
      availableFilters,
      selectedFilters: getEmptySelections(availableFilters).map(
        (empty) =>
          (categoryChanged && empty.fieldName === 'documentTypeId'
            ? empty
            : prev.selectedFilters.find((item) => item.fieldName === empty.fieldName)) ?? empty
      ),
    }));
  }, [categories, selectedCategoryId, setFiltersState]);

  return (
    <FilterModal
      onClose={goBack}
      sliderModalWidth={isMobile ? 1 : isTablet ? 0.5 : 0.3}
      filterAtom={reportGeneratorFiltersAtom}
    />
  );
};
